import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export type IngestKind =
  | 'COLLECTION_LIST'
  | 'CUSTOMER_MASTER'
  | 'LOAN_MASTER'
  | 'DPD_DUES'
  | 'PRIORITY_DELTA'
  | 'DNC_DELTA'
  | 'ALLOCATION_DELTA'

/** The allocation slot a bulk loan allocation file is for. */
export type AllocationSlot = 'FIELD' | 'TELE'

export interface BatchSummary {
  batchId: string
  kind: string
  status: string
  fileName: string
  rowCount: number | null
  acceptedCount: number
  rejectedCount: number
  submittedAt: string | null
  completedAt: string | null
  /** First row error's message, so the list can say why — null when nothing was rejected. */
  topRejectionReason: string | null
  /** Whole-file upload choices, e.g. `{ slot: 'FIELD' }` for bulk loan allocation; empty otherwise. */
  options?: Record<string, string>
}

export interface BatchStatus extends BatchSummary {
  fileSha256: string
  startedAt: string | null
  failureReason: string | null
  /** First 500 row errors (server-truncated) as a JSON array string. */
  rejectionReportJson: string | null
  /** Full error count before truncation; null when there is no report. */
  totalErrorCount: number | null
}

export interface UploadResponse {
  batchId: string
  kind: string
  status: 'QUEUED' | 'DUPLICATE'
  fileName: string
  sizeBytes: number
  /** Explanation on DUPLICATE — says nothing was processed. Null otherwise. */
  message: string | null
}

export interface TemplateColumn {
  name: string
  required: boolean
  help: string
}

export interface TemplateSummary {
  kind: IngestKind
  displayName: string
  fileName: string
  columns: TemplateColumn[]
}

export function useTemplates() {
  return useQuery({
    queryKey: ['ingest', 'templates'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get<TemplateSummary[]>('/v1/ingest/templates')
      return res.data
    },
  })
}

/**
 * Downloads the batch's rejected rows as .xlsx and saves it.
 *
 * Goes through the axios client rather than a plain anchor href because the
 * endpoint is authenticated — an <a download> would send no bearer token and
 * come back 401.
 */
export async function downloadFailedRowsXlsx(batchId: string): Promise<void> {
  const res = await api.get<Blob>(`/v1/ingest/batches/${batchId}/failed-rows.xlsx`, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = `failed-rows-${batchId}.xlsx`
    a.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Returns the URL of the .xlsx template — use as a plain anchor href. */
export function templateDownloadUrl(kind: IngestKind): string {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8080'
  return `${base}/v1/ingest/templates/${kind}.xlsx`
}

export function useBatches() {
  return useQuery({
    queryKey: ['ingest', 'batches'],
    queryFn: async () => {
      const res = await api.get<BatchSummary[]>('/v1/ingest/batches', { params: { limit: 50 } })
      return res.data
    },
    refetchInterval: (q) => {
      // Auto-refresh while any batch is still running
      const rows = (q.state.data as BatchSummary[] | undefined) ?? []
      return rows.some((b) => b.status === 'QUEUED' || b.status === 'RUNNING') ? 1500 : false
    },
  })
}

export function useBatch(batchId: string | null | undefined) {
  return useQuery({
    queryKey: ['ingest', 'batch', batchId],
    enabled: !!batchId,
    queryFn: async () => {
      const res = await api.get<BatchStatus>(`/v1/ingest/batches/${batchId}`)
      return res.data
    },
    refetchInterval: (q) => {
      const b = q.state.data as BatchStatus | undefined
      return b && (b.status === 'QUEUED' || b.status === 'RUNNING') ? 1000 : false
    },
  })
}

export function useUploadBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { kind: IngestKind; file: File; force?: boolean; slot?: AllocationSlot }) => {
      const form = new FormData()
      form.append('kind', vars.kind)
      form.append('file', vars.file)
      // Bulk loan allocation only: the slot every row of the file goes to.
      if (vars.slot) form.append('slot', vars.slot)
      // Re-process a file already uploaded for this kind. Without it the
      // server returns the original batch and does no work.
      if (vars.force) form.append('force', 'true')
      const res = await api.post<UploadResponse>('/v1/ingest/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingest', 'batches'] })
    },
  })
}
